import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutGrid, LayoutTemplate, Palette, Sparkles, Wand2, BookOpen, Ruler, LogOut, Menu, X, Paintbrush } from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutGrid },
  { to: "/templates", label: "Templates", icon: LayoutTemplate },
  { to: "/brand-kit", label: "Brand Kit", icon: Palette },
  { to: "/marketing", label: "AI Marketing", icon: Sparkles },
  { to: "/ai-images", label: "AI Images", icon: Wand2 },
  { to: "/recolor", label: "Recolor", icon: Paintbrush },
  { to: "/manuals", label: "Manuals", icon: BookOpen },
  { to: "/technical-drawing", label: "Technical Drawing", icon: Ruler },
];

export default function Sidebar() {
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => { setOpen(false); }, [pathname]);

  const Brand = () => (
    <>
      <div className="logo-font text-2xl md:text-3xl text-kann-text-pri leading-none" data-testid="brand-logo">KANN<span className="text-kann-gold">.</span></div>
      <div className="font-light text-[11px] md:text-xs text-kann-text-sec mt-1 tracking-wide">Brand &amp; Design Studio</div>
    </>
  );

  const Nav = ({ onItem }) => (
    <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
      {NAV.map(({ to, label, icon: Icon }) => {
        const active = pathname === to;
        return (
          <Link key={to} to={to} onClick={onItem} data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
            className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm transition-colors ${active ? "bg-kann-ink text-white font-semibold" : "text-kann-text-sec hover:bg-kann-surface-active hover:text-kann-text-pri"}`}>
            <Icon className="w-[18px] h-[18px]" />
            {label}
          </Link>
        );
      })}
    </nav>
  );

  const Footer = () => (
    <div className="p-3 border-t border-kann-border/20">
      <div className="px-4 py-2">
        <div className="text-sm text-kann-text-pri truncate font-medium">{user?.name}</div>
        <div className="text-xs text-kann-text-sec truncate">{user?.email}</div>
      </div>
      <button onClick={() => logout().then(() => navigate("/login"))} data-testid="logout-btn"
        className="w-full flex items-center gap-3 px-4 py-3 rounded-md text-sm text-kann-text-sec hover:bg-kann-surface-active hover:text-kann-text-pri transition-colors">
        <LogOut className="w-[18px] h-[18px]" /> Sign out
      </button>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 h-screen sticky top-0 bg-kann-surface border-r border-kann-border/30 flex-col" data-testid="app-sidebar">
        <div className="px-6 py-7 border-b border-kann-border/20"><Brand /></div>
        <Nav />
        <Footer />
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden fixed top-0 inset-x-0 z-40 h-14 bg-kann-surface border-b border-kann-border/30 flex items-center justify-between px-4" data-testid="mobile-topbar">
        <Link to="/dashboard" className="logo-font text-2xl text-kann-text-pri leading-none">KANN<span className="text-kann-gold">.</span></Link>
        <button onClick={() => setOpen(true)} data-testid="mobile-menu-btn" aria-label="Menu" className="p-2 -mr-2 rounded-md text-kann-text-pri hover:bg-kann-surface-active active:bg-kann-surface-active">
          <Menu className="w-6 h-6" />
        </button>
      </header>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden fixed inset-0 z-50" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/50 animate-in fade-in" onClick={() => setOpen(false)} data-testid="mobile-drawer-backdrop" />
          <aside className="absolute left-0 top-0 h-full w-[82%] max-w-xs bg-kann-surface border-r border-kann-border/30 flex flex-col shadow-2xl animate-in slide-in-from-left duration-200">
            <div className="flex items-center justify-between px-5 py-4 border-b border-kann-border/20">
              <div><Brand /></div>
              <button onClick={() => setOpen(false)} data-testid="mobile-drawer-close" aria-label="Sluiten" className="p-2 rounded-md text-kann-text-pri hover:bg-kann-surface-active"><X className="w-5 h-5" /></button>
            </div>
            <Nav onItem={() => setOpen(false)} />
            <Footer />
          </aside>
        </div>
      )}
    </>
  );
}
